import { Theme } from '@/interfaces/IThemeInterface';

export interface FeatureConfigs {
	featureVisibilitySidebar?: string[];
	featureTrialVisibilitySideBar?: string[];
	featureGatingSidebar?: string[];
	featureTrialGatingSideBar?: string[];
	configureVisibility?: string[];
	configureVisibilityTrial?: string[];
	configureGating?: string[];
	configureGatingTrial?: string[];
	advanceFeaturesVisibility?: string[];
	advanceFeaturesVisibilityTrial?: string[];
	advanceFeaturesGating?: string[];
	advanceFeaturesGatingTrial?: string[];
}

export interface Features {
	visibility: Partial<FeatureConfigs>;
	gating: Partial<FeatureConfigs>;
}

export type TutorialSteps = {
	receptionist_tutorial: boolean;
	import_twilio_tutorial: boolean;
	plan_creation_tutorial: boolean;
	branding_tutorial: boolean;
	email_tutorial: boolean;
	phone_number_tutorial: boolean;
	subscription_tutorial: boolean;
	custom_domain_tutorial: boolean;
	webhooks_tutorial: boolean;
};

export type Tours = {
	receptionist: boolean;
	branding: boolean;
	tutorials: boolean;
	contact_support: boolean;
	phone_numbers: boolean;
	email: boolean;
	subscription: boolean;
	academy: boolean;
	twilio: boolean;
	plan_creation: boolean;
};

export enum WebhookEvent {
	CLIENT_CREATED = 'client_created', // when a new client account is created
	CLIENT_PAYMENT_FAILED = 'client_payment_failed', // when a client payment fails
	CLIENT_CHURNED = 'client_churned', // when a client account is churned
	TRIAL_ACCOUNT_CONVERTED = 'trial_account_converted', // when a trial account is converted to a paid account
	CALL_MADE = 'call_made', // when a call is made by any of the client account
	TEXT_MADE = 'text_made', // when a text is made by any of the client account

	//   USAGE_LIMITS_EXCEEDED = 'usage_limits_exceeded', // when a client account exceeds their usage limits

	//   TEST_EVENT = 'test_event', // this is only for dev environment
}

export enum WebhookType {
	SLACK = 'slack',
	API = 'api',
}

export type Webhook = {
	id: string;
	url: string;
	title: string;
	description?: string;
	events: WebhookEvent[];
	status: 'active' | 'inactive';
	createdAt: string;
	updatedAt?: string;
	type: WebhookType;
};

export type Reseller = {
	auto_scale_receptionists: boolean;

	customDomain?: string;
	amplifyAppId?: string;
	amplifyRegion?: string;
	timestamp: string;
	reseller_type: string;
	theme_color: string;
	termsOfUseURL: string;
	privacyPolicyURL: string;
	branded: boolean;
	permanently_unbranded?: boolean;
	showResellerLoginEmbedTile?: boolean;
	needPaymentClearance?: boolean;
	customButton1Label?: string;
	customButton1URL?: string;
	customButton2Label?: string;
	customButton2URL?: string;
	logoDimensions?: {
		width: number;
		height: number;
	};
	purchaseRedirectLink?: string;
	dashboardTheme?: Partial<Theme>;
	featureVisibility?: Array<string>;
	featureGating?: Array<string>;
	featureTrialVisibility?: Array<string>;
	featureTrialGating?: Array<string>;
	features?: Features;
	resellerView?: boolean;
	is_terms_accepted?: boolean;
	inboundTwilioConfig?: {
		accountId: string;
		accessToken: string;
	};
	outboundTwilioConfig?: {
		accountId: string;
		accessToken: string;
	};
	stripe_connect_id?: string;
	webhook_url?: string;
	allowOutboundAccess?: boolean;
	onboardingStep?: number;
	isOnboardingComplete?: boolean;
	hasCompletedOnboarding?: boolean;
	tutorial_steps?: TutorialSteps;
	defaultRegistration?: string;
	firstName?: string;
	lastName?: string;

	id: string;
	username: string;
	emailAddress?: string;
	googleId?: string;
	csm?: string;
	customerId: string;
	inboundSubscriptionId?: string;
	inboundSubscribedProductId?: string;
	inboundSubscriptionStatus?: string;
	inboundSubscriptionTier?: string;
	outboundSubscriptionId?: string;
	outboundSubscribedProductId?: string;
	outboundSubscriptionStatus?: string;
	voiceSubscriptionId?: string;
	voiceSubscriptionStatus?: string;
	inboundDemo: boolean;
	outboundDemo: boolean;
	userType: 'Client' | 'Reseller' | 'SubAccount';
	totalInboundAgents: number;
	unConfiguredInboundAgents: number;
	configuredInboundAgents: number;
	totalOutboundAgents: number;
	unConfiguredOutboundAgents: number;
	configuredOutboundAgents: number;
	minimumAllowedInboundAgents?: number;
	minimumAllowedOutboundAgents?: number;
	timeZone?: string;
	reactivated?: boolean;
	token?: string;
	resellerId?: string;
	businessRevenue?: number;
	totalNumberOfCalls?: number;
	totalCallDuration?: number;
	businessNoOfLocations?: number;
	lastSeatPurchasedOn?: Date;
	inboundCanceledAt?: Date;
	outboundCanceledAt?: Date;
	voiceCanceledAt?: Date;
	trialEndedOn?: Date;
	country?: string;
	findOutAboutUs?: string;
	isDeleted?: boolean;
	academyHistory?: Record<string, any>;
	currentVoiceCycleStart?: string;
	currentVoiceCycleEnd?: string;
	resellerClient?: boolean;
	retentionCouponUsed?: boolean;
	businessName?: string;
	termsAccepted?: boolean;
	logo?: string;
	logoUrl?: string;
	businessEmployeeSize?: number;
	inboundScheduleSubCancelOn?: string;
	outboundScheduleSubCancelOn?: string;
	removeWatermark?: boolean;
	showWaterMarkRemoval?: boolean;
	intercom_app_id?: string;
	tours?: Tours;
	twilioBundleId?: string;
	twilioBundleStatus?: string;
	mailgunCreds?: {
		domain: string;
		address: string;
		email: string;
		username: string;
		apiKey: string;
		region?: string;
	};
	unlimitedInboundAgents?: boolean;
	unlimitedOutboundAgents?: boolean;
	createdAt?: Date;
};

export type ResellerInitialState = {
	data: Reseller;
	clients: any[];
	isAuthenticated: boolean;
	authToken: string | undefined;
	isTrialReseller?: boolean;
};
